// Email composer + real Gmail conversation + open-tracking history for a CRM contact, sent through the
// person's own connected Gmail (see connectors/google.ts and modules/crm/email.ts). Two different, both
// honest, sources: the live Gmail thread (fetched on demand, never stored) shows the real back-and-forth
// including the contact's replies; the Unitly-sent history below tracks pixel-based open detection, which
// the live thread can't show (Gmail doesn't expose that at all) - see CrmSentEmail.opens.
import { useState } from 'react';
import { ArrowDownLeft, ArrowUpRight, Mail, MailOpen, Reply, Send } from 'lucide-react';
import type { GmailThreadMessage, Workspace } from '@unitly/contracts';
import { useConnectorAction, useCrmEmails, useGmailStatus, useGmailThread, useSendCrmEmail } from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { useToast } from '../../ui/Toast.tsx';
import { ApiError } from '../../api/client.ts';

/** Starts the OAuth flow for this workspace's own CRM Gmail connection (private to whoever connects it -
 * a colleague in the same workspace never sees it, and it's separate from any other workspace's). */
function useConnectWorkspaceGmail(workspaceId: string) {
  const { orgId } = useOrgCtx();
  const connect = useConnectorAction(orgId);
  const toast = useToast();
  return () => connect.mutate({ action: 'authorize', scope: 'workspace_user', workspaceId }, {
    onSuccess: (res) => { if (res && 'url' in res) window.location.href = res.url; },
    onError: () => toast('Non è stato possibile avviare il collegamento con Google'),
  });
}

function GmailBanner({ workspace, status }: { workspace: Workspace; status: { connected: boolean; needsReconnect: boolean; email: string | null } | undefined }) {
  const connectGmail = useConnectWorkspaceGmail(workspace.id);
  if (status?.connected) return null;
  return (
    <div className="card card-pad" style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, color: 'var(--label-2)' }}>
      <Mail size={16} />
      <span style={{ flex: 1 }}>
        {status?.needsReconnect
          ? 'Il tuo account Google per il CRM di questo spazio va riautorizzato.'
          : 'Collega la tua email per inviare messaggi da questo contatto (solo tu la vedrai, anche se altri nello spazio usano lo stesso indirizzo).'}
      </span>
      <button type="button" className="btn btn-secondary btn-sm" style={{ whiteSpace: 'nowrap' }} onClick={connectGmail}>
        {status?.needsReconnect ? 'Ricollega' : 'Collega Google'}
      </button>
    </div>
  );
}

function GmailThreadPanel({ workspace, contactId, canRead, onReply }: { workspace: Workspace; contactId: string; canRead: boolean; onReply: (m: GmailThreadMessage) => void }) {
  const { orgId } = useOrgCtx();
  const connectGmail = useConnectWorkspaceGmail(workspace.id);
  const [load, setLoad] = useState(false);
  const thread = useGmailThread(orgId, workspace.id, contactId, load);

  return (
    <div className="card card-pad">
      <div className="toolbar" style={{ padding: 0, margin: 0 }}>
        <strong style={{ fontSize: 13.5 }}>Conversazione Gmail</strong>
        <span className="spacer" />
        {canRead ? (
          <button type="button" className="btn btn-secondary btn-sm" disabled={thread.isFetching}
            onClick={() => (load ? thread.refetch() : setLoad(true))}>
            {thread.isFetching ? 'Carico…' : load ? 'Aggiorna' : 'Carica conversazione'}
          </button>
        ) : (
          <button type="button" className="btn btn-secondary btn-sm" style={{ whiteSpace: 'nowrap' }} onClick={connectGmail}>
            Ricollega per leggerla
          </button>
        )}
      </div>
      {thread.isError && <p className="hint" style={{ margin: '8px 0 0' }}>Non è stato possibile caricare la conversazione da Gmail.</p>}
      {load && thread.data?.length === 0 && <p className="hint" style={{ margin: '8px 0 0' }}>Nessuna email trovata con questo indirizzo (ultimi 20 messaggi).</p>}
      {load && thread.data && thread.data.length > 0 && (
        <div className="list" style={{ marginTop: 8 }}>
          {thread.data.map((m) => (
            <div key={m.id} className="list-row" style={{ cursor: 'default', alignItems: 'flex-start' }}>
              <span className="list-icon" style={{ background: m.direction === 'out' ? 'var(--ws-indigo)' : 'var(--ws-teal)' }}>
                {m.direction === 'out' ? <ArrowUpRight size={15} /> : <ArrowDownLeft size={15} />}
              </span>
              <span className="grow">
                <div><strong>{m.subject || '(senza oggetto)'}</strong></div>
                <div className="secondary">{m.direction === 'out' ? 'Tu' : m.from} · {m.date ? new Date(m.date).toLocaleString('it-IT') : '—'}</div>
                <div className="secondary" style={{ whiteSpace: 'pre-wrap' }}>{m.bodyText || m.snippet}</div>
                <button type="button" className="btn btn-plain btn-sm" style={{ padding: '2px 0', height: 'auto' }} onClick={() => onReply(m)}>
                  <Reply size={13} /> Rispondi
                </button>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function EmailHistory({ workspace, contactId, dealId }: { workspace: Workspace; contactId?: string; dealId?: string }) {
  const { orgId } = useOrgCtx();
  const emails = useCrmEmails(orgId, workspace.id, { contactId, dealId });

  if (emails.isLoading) return null;
  if (!emails.data?.length) return <p className="hint" style={{ padding: '4px 0', margin: 0 }}>Nessuna email inviata.</p>;

  return (
    <div className="list" style={{ marginBottom: 10 }}>
      {emails.data.map((e) => (
        <div key={e.id} className="list-row" style={{ cursor: 'default', alignItems: 'flex-start' }}>
          <span className="list-icon" style={{ background: 'var(--ws-blue)' }}>{e.opens.length > 0 ? <MailOpen size={16} /> : <Mail size={16} />}</span>
          <span className="grow">
            <div><strong>{e.subject}</strong> · {e.sentBy.displayName}</div>
            <div className="secondary">A {e.toEmail} · {new Date(e.sentAt).toLocaleString('it-IT')}</div>
            <div className="secondary" style={{ whiteSpace: 'pre-wrap' }}>{e.body}</div>
            <div className="secondary" style={{ color: e.opens.length > 0 ? 'var(--green)' : undefined }}>
              {e.opens.length > 0
                ? `Aperta il ${new Date(e.opens[0]!.openedAt).toLocaleString('it-IT')}`
                : 'Non ancora aperta (indicazione approssimativa: alcuni client bloccano le immagini)'}
            </div>
          </span>
        </div>
      ))}
    </div>
  );
}

export function EmailComposer({ workspace, contactId, dealId, contactEmail }: { workspace: Workspace; contactId: string; dealId?: string; contactEmail: string | null }) {
  const { orgId } = useOrgCtx();
  const status = useGmailStatus(orgId, workspace.id);
  const send = useSendCrmEmail(orgId, workspace.id);
  const toast = useToast();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [replyTo, setReplyTo] = useState<{ threadId: string; inReplyTo: string | null; subject: string } | null>(null);

  const startReply = (m: GmailThreadMessage) => {
    setReplyTo({ threadId: m.threadId, inReplyTo: m.messageId, subject: m.subject });
    setSubject(m.subject && !/^re:/i.test(m.subject) ? `Re: ${m.subject}` : m.subject || 'Re:');
    setBody('');
  };
  const cancelReply = () => { setReplyTo(null); setSubject(''); };

  return (
    <>
      <GmailBanner workspace={workspace} status={status.data} />
      {!contactEmail && <p className="hint" style={{ padding: 0, margin: 0 }}>Questo contatto non ha un indirizzo email registrato.</p>}
      {contactEmail && status.data?.connected && (
        <GmailThreadPanel workspace={workspace} contactId={contactId} canRead={Boolean(status.data?.canReadThread)} onReply={startReply} />
      )}
      {contactEmail && status.data?.connected && (
        <>
          {replyTo && (
            <div className="secondary" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Reply size={13} /> In risposta a “{replyTo.subject}”
              <button type="button" className="btn btn-plain btn-sm" style={{ padding: '0 6px', height: 20 }} onClick={cancelReply}>Annulla</button>
            </div>
          )}
          <input className="input" placeholder="Oggetto" value={subject} onChange={(e) => setSubject(e.target.value)} />
          <textarea className="textarea" rows={4} placeholder={`Scrivi un messaggio a ${contactEmail}…`} value={body} onChange={(e) => setBody(e.target.value)} />
          <div className="toolbar" style={{ padding: 0 }}>
            <span className="spacer" />
            <button type="button" className="btn btn-primary btn-sm" disabled={!subject.trim() || !body.trim() || send.isPending}
              onClick={() => send.mutate(
                { contactId, dealId: dealId ?? null, subject, body, threadId: replyTo?.threadId ?? null, inReplyTo: replyTo?.inReplyTo ?? null },
                {
                  onSuccess: () => { setSubject(''); setBody(''); setReplyTo(null); toast('Email inviata'); },
                  onError: (e) => toast(e instanceof ApiError ? e.message : 'Invio non riuscito'),
                },
              )}>
              <Send size={14} /> {replyTo ? 'Rispondi' : 'Invia'}
            </button>
          </div>
        </>
      )}
      <EmailHistory workspace={workspace} contactId={contactId} dealId={dealId} />
    </>
  );
}
